import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { CHAPTER_NAMES, getDonationLinks } from '../../data/chapterConfig';

// Donation URL per chapter: the /chapters doc's stripeLinks.donationUrl
// (written by the New Chapter wizard) wins over the static chapterConfig
// link, so wizard-created chapters show up here with no deploy. Cached at
// module level — the chooser mounts on several surfaces per page.
let chapterDocsPromise = null;
function loadChapterDonationDocs() {
  if (!chapterDocsPromise) {
    chapterDocsPromise = getDocs(collection(db, 'chapters'))
      .then((snap) => snap.docs
        .map((d) => d.data())
        .filter((c) => c.active !== false)
        .sort((a, b) => {
          const ao = typeof a.order === 'number' ? a.order : 999;
          const bo = typeof b.order === 'number' ? b.order : 999;
          if (ao !== bo) return ao - bo;
          return (a.name || '').localeCompare(b.name || '');
        }))
      .catch((err) => {
        console.error('ChapterDonationChooser: chapters load failed:', err);
        chapterDocsPromise = null;
        return null;
      });
  }
  return chapterDocsPromise;
}

export default function ChapterDonationChooser({ renderTrigger, accent = 'ink' }) {
  const [open, setOpen] = useState(false);
  const [chapterDocs, setChapterDocs] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadChapterDonationDocs().then((docs) => {
      if (!cancelled && docs) setChapterDocs(docs);
    });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) {
    return renderTrigger(() => setOpen(true));
  }

  // Firestore list when available (includes new chapters), else the static
  // registry. Both paths hide chapters with no donation URL.
  const chapterEntries = (chapterDocs && chapterDocs.length > 0)
    ? chapterDocs.map((c) => ({
        name: c.name,
        url: c.stripeLinks?.donationUrl || getDonationLinks(c.name)?.oneTimeUrl,
      }))
    : CHAPTER_NAMES.map((name) => ({ name, url: getDonationLinks(name)?.oneTimeUrl }));

  // Chrome window: raised bevel frame, gradient titlebar (hue follows the
  // accent prop), bevelled chapter buttons.
  return (
    <div
      role="dialog"
      aria-label="Choose your chapter"
      style={{
        background: 'var(--chrome-face)',
        border: '2px solid',
        borderColor: 'var(--bevel-raised)',
        boxShadow: 'var(--shadow-raised)',
        padding: 3,
      }}
    >
      <div
        className={`win95-titlebar${accent === 'aqua' ? ' win95-titlebar--aqua' : ''}`}
        style={{ cursor: 'default' }}
      >
        <span className="win95-titlebar__label">Choose your chapter</span>
        <div className="win95-titlebar__btns">
          <button
            type="button"
            className="win95-btn-sys win95-close-btn"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 6, padding: '10px 8px 8px' }}>
        {chapterEntries.map(({ name, url }) => {
          if (!url) return null;
          return (
            <a
              key={name}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="win95-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                textDecoration: 'none',
                // Inline so the global a:visited purple can't outrank .win95-btn
                color: 'var(--chrome-outline)',
              }}
            >
              {name}
            </a>
          );
        })}
      </div>
    </div>
  );
}
